import { api } from "../api";
import { openModal, closeModal, modalBody, setModalTitle } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { canWrite, writeOnly, readOnlyNotice, lockInputs } from "../utils/permissions";
import { showToast } from "../utils/toast";
import { isoDate, addDays, parseIso, formatDate, WEEKDAY_NAMES, isoWeekday } from "../utils/date-utils";
import type { Lecturer, LecturerAvailability, LecturerException, Lesson } from "../types";
import { esc } from "../utils/html";
import { openCsvImportModal } from "../components/csv-import-modal";

/**
 * Dozentenverwaltung. Ein Dozent kann mit einem Benutzer der Schule verknüpft werden;
 * dann ist die Benutzerverwaltung führend und Name und E-Mail sind hier gesperrt.
 * Anwesenheiten sind Serien (Wochentag, optionales Zeitfenster, Rhythmus), Ausnahmen
 * überschreiben sie für einzelne Tage oder Zeiträume.
 */

let lecturers: Lecturer[] = [];
let userOptions: { id: string; firstName: string; lastName: string; email: string }[] = [];
let currentEl: HTMLElement | null = null;
let searchQuery = "";

async function load() {
  lecturers = await api.stundenplan.lecturers.list();
  if (canWrite()) {
    // Ohne Benutzerliste lässt sich nur nicht verknüpfen; die Seite bleibt nutzbar.
    try { userOptions = await api.stundenplan.lecturers.userOptions(); }
    catch { userOptions = []; }
  }
}

function fullName(l: Lecturer): string {
  return `${l.firstName} ${l.lastName}`.trim();
}

/** "Mo 08:00 bis 16:00, alle 2 Wochen" als eine lesbare Zeile. */
function availabilityText(a: LecturerAvailability): string {
  const day = WEEKDAY_NAMES[a.weekday - 1] || "?";
  const time = a.startTime && a.endTime ? `${a.startTime} bis ${a.endTime}` : "ganztags";
  const rhythm = a.intervalWeeks > 1 ? `alle ${a.intervalWeeks} Wochen ab ${formatDate(a.anchorDate)}` : "jede Woche";
  const range = a.validFrom || a.validTo
    ? ` (${a.validFrom ? `ab ${formatDate(a.validFrom)}` : ""}${a.validFrom && a.validTo ? ", " : ""}${a.validTo ? `bis ${formatDate(a.validTo)}` : ""})`
    : "";
  return `${day}, ${time}, ${rhythm}${range}`;
}

function exceptionText(e: LecturerException): string {
  const kind = e.kind === "present" ? "Ausnahmsweise da" : "Nicht da";
  const range = e.startDate === e.endDate
    ? formatDate(e.startDate)
    : `${formatDate(e.startDate)} bis ${formatDate(e.endDate)}`;
  const time = e.startTime && e.endTime ? `, ${e.startTime} bis ${e.endTime}` : "";
  return `${kind}: ${range}${time}`;
}

function renderTable() {
  const tbody = currentEl?.querySelector("tbody");
  if (!tbody) return;

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? lecturers.filter(l => [l.firstName, l.lastName, l.email].some(v => (v || "").toLowerCase().includes(q)))
    : lecturers;

  const cols = canWrite() ? 5 : 4;
  if (filtered.length === 0) {
    tbody.innerHTML = `<tr><td colspan="${cols}" class="empty-state">${q ? "Keine Treffer" : "Keine Dozenten vorhanden"}</td></tr>`;
    return;
  }

  tbody.innerHTML = filtered.map(l => `
    <tr style="cursor:pointer" data-open="${l.id}">
      <td style="font-weight:500">
        ${esc(fullName(l))}
        ${l.userMissing ? `<span class="badge" style="background:#fee2e2;color:#991b1b" title="Der verknüpfte Benutzer existiert nicht mehr">Benutzer fehlt</span>` : ""}
      </td>
      <td class="text-muted">${esc(l.email || "")}</td>
      <td>
        ${l.isExternal ? `<span class="badge badge-blue">Extern</span>` : `<span class="text-muted">Intern</span>`}
        ${l.active ? "" : `<span class="badge" title="Steht im Stundenplan nicht zur Auswahl">Inaktiv</span>`}
      </td>
      <td>${l.keycloakUserId ? `<span class="badge badge-green">Verknüpft</span>` : `<span class="text-muted">Nicht verknüpft</span>`}</td>
      ${writeOnly(`<td style="white-space:nowrap"><button class="btn btn-danger btn-sm" data-del="${l.id}">Löschen</button></td>`)}
    </tr>`).join("");
}

// ── Dozenten-Modal mit drei Bereichen ──

type LecturerTab = "stammdaten" | "anwesenheiten" | "ausnahmen" | "unterricht";

const SPINNER = `<div style="display:flex;align-items:center;justify-content:center;height:160px"><div class="spinner"></div></div>`;

/**
 * Ein einziges Fenster für den Dozenten. Stammdaten, Anwesenheiten und Ausnahmen sind
 * Tabs im selben Modal, es wird also nie ausgetauscht.
 *
 * Anwesenheiten und Ausnahmen hängen an einer gespeicherten Dozenten-ID. Beim Anlegen
 * ist deshalb zuerst nur Stammdaten da; sobald gespeichert ist, kommen die beiden
 * anderen Tabs dazu, ohne dass das Fenster schließt.
 */
function openLecturerModal(existing?: Lecturer, startTab: LecturerTab = "stammdaten") {
  let lecturer = existing;
  let activeTab: LecturerTab = lecturer ? startTab : "stammdaten";
  const readOnly = !canWrite();

  openModal(lecturer ? fullName(lecturer) : "Neuer Dozent", `
    <div class="detail-tabs" id="l-tabs"></div>
    <div id="l-content">${SPINNER}</div>`, { wide: true, fixedSize: true });

  const mb = modalBody();
  const tabsEl = mb.querySelector("#l-tabs") as HTMLElement;
  const content = mb.querySelector("#l-content") as HTMLElement;

  const renderTabs = () => {
    const tabs: { id: LecturerTab; label: string }[] = [{ id: "stammdaten", label: "Stammdaten" }];
    if (lecturer) {
      tabs.push({ id: "anwesenheiten", label: "Anwesenheiten" });
      tabs.push({ id: "ausnahmen", label: "Ausnahmen" });
      tabs.push({ id: "unterricht", label: "Unterricht" });
    }
    tabsEl.innerHTML = tabs
      .map(t => `<button class="detail-tab${t.id === activeTab ? " active" : ""}" data-tab="${t.id}">${t.label}</button>`)
      .join("");
    tabsEl.querySelectorAll<HTMLButtonElement>(".detail-tab").forEach(btn => {
      btn.addEventListener("click", () => showTab(btn.dataset.tab as LecturerTab));
    });
  };

  const showTab = async (tab: LecturerTab) => {
    activeTab = tab;
    renderTabs();
    if (tab === "stammdaten") { renderStammdaten(); return; }
    content.innerHTML = SPINNER;
    try {
      if (tab === "anwesenheiten") await renderAvailabilities(lecturer!, content);
      else if (tab === "ausnahmen") await renderExceptions(lecturer!, content);
      else await renderLessons(lecturer!, content);
    } catch (err: any) {
      content.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    }
  };

  const renderStammdaten = () => {
    const disabled = readOnly ? " disabled" : "";

    // Bereits verknüpfte Benutzer stehen nicht mehr zur Auswahl, außer beim eigenen Datensatz.
    const takenIds = new Set(lecturers.filter(l => l.id !== lecturer?.id).map(l => l.keycloakUserId).filter(Boolean));
    const userOpts = `<option value="">Kein Benutzer (externer oder nicht angelegter Dozent)</option>` +
      userOptions
        .filter(u => !takenIds.has(u.id) || u.id === lecturer?.keycloakUserId)
        .map(u => `<option value="${esc(u.id)}" ${lecturer?.keycloakUserId === u.id ? "selected" : ""}>${esc(`${u.firstName} ${u.lastName}`.trim())} (${esc(u.email)})</option>`)
        .join("");

    content.innerHTML = `
      <div class="form-group">
        <label>Benutzer</label>
        <select id="f-user"${disabled}>${userOpts}</select>
        <small class="text-muted">Bei verknüpftem Benutzer werden Name und E-Mail in der Benutzerverwaltung gepflegt.</small>
      </div>
      <div class="form-grid">
        <div class="form-group"><label>Vorname *</label><input id="f-fn" value="${esc(lecturer?.firstName || "")}"${disabled} /></div>
        <div class="form-group"><label>Nachname *</label><input id="f-ln" value="${esc(lecturer?.lastName || "")}"${disabled} /></div>
      </div>
      <div class="form-group"><label>E-Mail</label><input type="email" id="f-mail" value="${esc(lecturer?.email || "")}"${disabled} /></div>
      <div class="form-group"><label>Notiz</label><textarea id="f-note" rows="2"${disabled}>${esc(lecturer?.note || "")}</textarea></div>
      <div class="form-group">
        <label class="checkbox-label"><input type="checkbox" id="f-external" ${lecturer?.isExternal ? "checked" : ""}${disabled} /> Externer Dozent</label>
      </div>
      <div class="form-group">
        <label class="checkbox-label"><input type="checkbox" id="f-active" ${lecturer?.active !== false ? "checked" : ""}${disabled} /> Aktiv</label>
      </div>
      <div class="alert alert-error hidden" id="f-modal-error"></div>
      <div class="form-actions">
        <button class="btn btn-secondary" id="f-cancel">${readOnly ? "Schließen" : "Abbrechen"}</button>
        ${writeOnly(`<button class="btn btn-primary" id="f-save">Speichern</button>`)}
      </div>`;

    const userSelect = content.querySelector("#f-user") as HTMLSelectElement;
    const fn = content.querySelector("#f-fn") as HTMLInputElement;
    const ln = content.querySelector("#f-ln") as HTMLInputElement;
    const mail = content.querySelector("#f-mail") as HTMLInputElement;

    // Bei verknüpftem Benutzer sind Name und Mail gesperrt: sonst gäbe es zwei Wahrheiten.
    const applyUserLock = () => {
      const user = userOptions.find(u => u.id === userSelect.value);
      const locked = !!user;
      if (user) {
        fn.value = user.firstName;
        ln.value = user.lastName;
        mail.value = user.email;
      }
      [fn, ln, mail].forEach(input => { input.disabled = locked || readOnly; });
    };
    applyUserLock();
    if (!readOnly) userSelect.addEventListener("change", applyUserLock);

    content.querySelector("#f-cancel")!.addEventListener("click", closeModal);
    if (readOnly) { lockInputs(content); return; }

    content.querySelector("#f-save")!.addEventListener("click", async () => {
      const data = {
        keycloakUserId: userSelect.value || null,
        firstName: fn.value.trim(),
        lastName: ln.value.trim(),
        email: mail.value.trim() || null,
        isExternal: (content.querySelector("#f-external") as HTMLInputElement).checked,
        active: (content.querySelector("#f-active") as HTMLInputElement).checked,
        note: (content.querySelector("#f-note") as HTMLTextAreaElement).value.trim() || null,
      };
      if (!data.firstName || !data.lastName) {
        showModalError(content, "Vor- und Nachname sind Pflichtfelder");
        return;
      }

      const saveBtn = content.querySelector("#f-save") as HTMLButtonElement;
      saveBtn.disabled = true; saveBtn.textContent = "Speichern…";
      try {
        const wasNew = !lecturer;
        const saved = lecturer
          ? await api.stundenplan.lecturers.update(lecturer.id, data)
          : await api.stundenplan.lecturers.create(data);
        lecturer = saved;
        setModalTitle(fullName(saved));

        await load();
        renderTable();

        // Das Fenster bleibt offen. Bei einem neuen Dozenten kommen jetzt die Tabs
        // Anwesenheiten und Ausnahmen dazu, weil die ID dafür erst hier feststeht.
        renderTabs();
        renderStammdaten();
        showToast(wasNew
          ? "Dozent angelegt, Anwesenheiten können jetzt gepflegt werden"
          : "Dozent gespeichert");
      } catch (err: any) {
        showModalError(content, err.message);
        saveBtn.disabled = false; saveBtn.textContent = "Speichern";
      }
    });
  };

  renderTabs();
  showTab(activeTab);
}

async function renderAvailabilities(lecturer: Lecturer, host: HTMLElement) {
  const items = await api.stundenplan.lecturers.availabilities.list(lecturer.id);
  host.innerHTML = `
    ${writeOnly(`<div style="margin-bottom:.75rem"><button class="btn btn-primary btn-sm" id="a-add">+ Anwesenheit</button></div>`)}
    ${items.length === 0
      ? `<div class="empty-state">Noch keine Anwesenheiten hinterlegt</div>`
      : `<div class="card table-wrap"><table><thead><tr><th>Regel</th><th>Notiz</th>${writeOnly(`<th style="width:180px"></th>`)}</tr></thead><tbody>
          ${items.map(a => `<tr>
            <td>${esc(availabilityText(a))}</td>
            <td class="text-muted">${esc(a.note || "")}</td>
            ${writeOnly(`<td style="white-space:nowrap">
              <button class="btn btn-secondary btn-sm" data-edit-a="${a.id}">Bearbeiten</button>
              <button class="btn btn-danger btn-sm" data-del-a="${a.id}">Löschen</button>
            </td>`)}
          </tr>`).join("")}
        </tbody></table></div>`}`;

  // Direkt an die Schaltflächen binden: host wird bei jedem Rendern neu befüllt, ein
  // Listener auf host würde sich sonst mit jedem Durchlauf stapeln.
  host.querySelector("#a-add")?.addEventListener("click", () => openAvailabilityForm(lecturer, host));
  host.querySelectorAll<HTMLElement>("[data-edit-a]").forEach(btn => {
    btn.addEventListener("click", () => {
      const item = items.find(a => a.id === +btn.dataset.editA!);
      if (item) openAvailabilityForm(lecturer, host, item);
    });
  });
  host.querySelectorAll<HTMLElement>("[data-del-a]").forEach(btn => {
    btn.addEventListener("click", async () => {
      try {
        await api.stundenplan.lecturers.availabilities.delete(lecturer.id, +btn.dataset.delA!);
        await renderAvailabilities(lecturer, host);
        showToast("Anwesenheit gelöscht");
      } catch (err: any) { showToast("Fehler: " + err.message); }
    });
  });
}

/**
 * Formular für eine Anwesenheitsregel. Läuft ohne verschachteltes Modal: der Bereich
 * wird an Ort und Stelle ersetzt, weil modal.ts nur einen Slot hat.
 */
function openAvailabilityForm(lecturer: Lecturer, host: HTMLElement, item?: LecturerAvailability) {
  const dayOpts = WEEKDAY_NAMES
    .map((d, i) => `<option value="${i + 1}" ${(item?.weekday ?? 1) === i + 1 ? "selected" : ""}>${d}</option>`).join("");
  const intervalOpts = [1, 2, 3, 4]
    .map(n => `<option value="${n}" ${(item?.intervalWeeks ?? 1) === n ? "selected" : ""}>${n === 1 ? "Jede Woche" : `Alle ${n} Wochen`}</option>`).join("");
  const allDay = !item?.startTime;

  host.innerHTML = `
    <h3 style="margin-top:0">${item ? "Anwesenheit bearbeiten" : "Neue Anwesenheit"}</h3>
    <div class="form-grid">
      <div class="form-group"><label>Wochentag</label><select id="a-day">${dayOpts}</select></div>
      <div class="form-group"><label>Rhythmus</label><select id="a-interval">${intervalOpts}</select></div>
    </div>
    <div class="form-group">
      <label class="checkbox-label"><input type="checkbox" id="a-allday" ${allDay ? "checked" : ""} /> Ganztags</label>
    </div>
    <div class="form-grid" id="a-times" ${allDay ? `style="display:none"` : ""}>
      <div class="form-group"><label>Von</label><input type="time" id="a-start" value="${esc(item?.startTime || "08:00")}" /></div>
      <div class="form-group"><label>Bis</label><input type="time" id="a-end" value="${esc(item?.endTime || "16:00")}" /></div>
    </div>
    <div class="form-group">
      <label>Startwoche</label>
      <input type="date" id="a-anchor" value="${esc(item?.anchorDate || isoDate(new Date()))}" />
      <small class="text-muted">Ab dieser Woche gilt die Regel. Bei „alle 2 Wochen" ist das die erste Woche.</small>
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Gültig ab (optional)</label><input type="date" id="a-from" value="${esc(item?.validFrom || "")}" /></div>
      <div class="form-group"><label>Gültig bis (optional)</label><input type="date" id="a-to" value="${esc(item?.validTo || "")}" /></div>
    </div>
    <div class="form-group"><label>Notiz</label><input id="a-note" value="${esc(item?.note || "")}" /></div>
    <div class="alert alert-error hidden" id="a-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="a-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="a-save">Speichern</button>
    </div>`;

  const allDayBox = host.querySelector("#a-allday") as HTMLInputElement;
  const times = host.querySelector("#a-times") as HTMLElement;
  allDayBox.addEventListener("change", () => { times.style.display = allDayBox.checked ? "none" : ""; });

  host.querySelector("#a-cancel")!.addEventListener("click", () => renderAvailabilities(lecturer, host));
  host.querySelector("#a-save")!.addEventListener("click", async () => {
    const data = {
      weekday: +(host.querySelector("#a-day") as HTMLSelectElement).value,
      intervalWeeks: +(host.querySelector("#a-interval") as HTMLSelectElement).value,
      startTime: allDayBox.checked ? null : (host.querySelector("#a-start") as HTMLInputElement).value,
      endTime: allDayBox.checked ? null : (host.querySelector("#a-end") as HTMLInputElement).value,
      anchorDate: (host.querySelector("#a-anchor") as HTMLInputElement).value,
      validFrom: (host.querySelector("#a-from") as HTMLInputElement).value || null,
      validTo: (host.querySelector("#a-to") as HTMLInputElement).value || null,
      note: (host.querySelector("#a-note") as HTMLInputElement).value.trim() || null,
    };
    try {
      if (item) await api.stundenplan.lecturers.availabilities.update(lecturer.id, item.id, data);
      else await api.stundenplan.lecturers.availabilities.create(lecturer.id, data);
      await renderAvailabilities(lecturer, host);
      showToast("Anwesenheit gespeichert");
    } catch (err: any) {
      const box = host.querySelector("#a-error") as HTMLElement;
      box.textContent = err.message;
      box.classList.remove("hidden");
    }
  });
}

async function renderExceptions(lecturer: Lecturer, host: HTMLElement) {
  const items = await api.stundenplan.lecturers.exceptions.list(lecturer.id);
  host.innerHTML = `
    ${writeOnly(`<div style="margin-bottom:.75rem"><button class="btn btn-primary btn-sm" id="e-add">+ Ausnahme</button></div>`)}
    ${items.length === 0
      ? `<div class="empty-state">Noch keine Ausnahmen hinterlegt</div>`
      : `<div class="card table-wrap"><table><thead><tr><th>Ausnahme</th><th>Grund</th>${writeOnly(`<th style="width:180px"></th>`)}</tr></thead><tbody>
          ${items.map(e => `<tr>
            <td>${esc(exceptionText(e))}</td>
            <td class="text-muted">${esc(e.reason || "")}</td>
            ${writeOnly(`<td style="white-space:nowrap">
              <button class="btn btn-secondary btn-sm" data-edit-e="${e.id}">Bearbeiten</button>
              <button class="btn btn-danger btn-sm" data-del-e="${e.id}">Löschen</button>
            </td>`)}
          </tr>`).join("")}
        </tbody></table></div>`}`;

  host.querySelector("#e-add")?.addEventListener("click", () => openExceptionForm(lecturer, host));
  host.querySelectorAll<HTMLElement>("[data-edit-e]").forEach(btn => {
    btn.addEventListener("click", () => {
      const item = items.find(x => x.id === +btn.dataset.editE!);
      if (item) openExceptionForm(lecturer, host, item);
    });
  });
  host.querySelectorAll<HTMLElement>("[data-del-e]").forEach(btn => {
    btn.addEventListener("click", async () => {
      try {
        await api.stundenplan.lecturers.exceptions.delete(lecturer.id, +btn.dataset.delE!);
        await renderExceptions(lecturer, host);
        showToast("Ausnahme gelöscht");
      } catch (err: any) { showToast("Fehler: " + err.message); }
    });
  });
}

function openExceptionForm(lecturer: Lecturer, host: HTMLElement, item?: LecturerException) {
  const allDay = !item?.startTime;
  host.innerHTML = `
    <h3 style="margin-top:0">${item ? "Ausnahme bearbeiten" : "Neue Ausnahme"}</h3>
    <div class="form-group">
      <label>Art</label>
      <select id="e-kind">
        <option value="absent" ${item?.kind !== "present" ? "selected" : ""}>Nicht da</option>
        <option value="present" ${item?.kind === "present" ? "selected" : ""}>Ausnahmsweise da</option>
      </select>
      <small class="text-muted">
        „Nicht da" streicht die Anwesenheiten in diesem Zeitraum, etwa bei Urlaub oder Fortbildung.
        „Ausnahmsweise da" ergänzt einen Termin außerhalb der Anwesenheiten.
      </small>
    </div>
    <div class="form-grid">
      <div class="form-group"><label>Von</label><input type="date" id="e-start" value="${esc(item?.startDate || isoDate(new Date()))}" /></div>
      <div class="form-group"><label>Bis</label><input type="date" id="e-end" value="${esc(item?.endDate || item?.startDate || isoDate(new Date()))}" /></div>
    </div>
    <div class="form-group">
      <label class="checkbox-label"><input type="checkbox" id="e-allday" ${allDay ? "checked" : ""} /> Ganztags</label>
    </div>
    <div class="form-grid" id="e-times" ${allDay ? `style="display:none"` : ""}>
      <div class="form-group"><label>Von</label><input type="time" id="e-tstart" value="${esc(item?.startTime || "08:00")}" /></div>
      <div class="form-group"><label>Bis</label><input type="time" id="e-tend" value="${esc(item?.endTime || "12:00")}" /></div>
    </div>
    <div class="form-group"><label>Grund</label><input id="e-reason" value="${esc(item?.reason || "")}" /></div>
    <div class="alert alert-error hidden" id="e-error"></div>
    <div class="form-actions">
      <button class="btn btn-secondary" id="e-cancel">Abbrechen</button>
      <button class="btn btn-primary" id="e-save">Speichern</button>
    </div>`;

  const allDayBox = host.querySelector("#e-allday") as HTMLInputElement;
  const times = host.querySelector("#e-times") as HTMLElement;
  allDayBox.addEventListener("change", () => { times.style.display = allDayBox.checked ? "none" : ""; });

  host.querySelector("#e-cancel")!.addEventListener("click", () => renderExceptions(lecturer, host));
  host.querySelector("#e-save")!.addEventListener("click", async () => {
    const data = {
      kind: (host.querySelector("#e-kind") as HTMLSelectElement).value,
      startDate: (host.querySelector("#e-start") as HTMLInputElement).value,
      endDate: (host.querySelector("#e-end") as HTMLInputElement).value,
      startTime: allDayBox.checked ? null : (host.querySelector("#e-tstart") as HTMLInputElement).value,
      endTime: allDayBox.checked ? null : (host.querySelector("#e-tend") as HTMLInputElement).value,
      reason: (host.querySelector("#e-reason") as HTMLInputElement).value.trim() || null,
    };
    try {
      if (item) await api.stundenplan.lecturers.exceptions.update(lecturer.id, item.id, data);
      else await api.stundenplan.lecturers.exceptions.create(lecturer.id, data);
      await renderExceptions(lecturer, host);
      showToast("Ausnahme gespeichert");
    } catch (err: any) {
      const box = host.querySelector("#e-error") as HTMLElement;
      box.textContent = err.message;
      box.classList.remove("hidden");
    }
  });
}


/**
 * Wann dieser Dozent Unterricht hat, nach Tagen gruppiert. Gegenstück zur Praktikums-
 * ansicht am Schüler: dort steht, wann er wo im Einsatz ist.
 *
 * Gezeigt werden die kommenden zwölf Wochen. Der Stundenplan selbst bleibt die
 * Klassenansicht; hier geht es nur um die Sicht einer Person.
 */
const LESSON_WEEKS = 12;

async function renderLessons(lecturer: Lecturer, host: HTMLElement) {
  const from = new Date();
  const von = isoDate(from);
  const bis = isoDate(addDays(from, LESSON_WEEKS * 7));
  const lessons = await api.stundenplan.plan(von, bis, { dozent: lecturer.id });

  if (lessons.length === 0) {
    host.innerHTML = `<div class="empty-state">
      In den nächsten ${LESSON_WEEKS} Wochen ist kein Unterricht eingeplant.
    </div>`;
    return;
  }

  const byDate = new Map<string, Lesson[]>();
  for (const lesson of lessons) {
    if (!byDate.has(lesson.lessonDate)) byDate.set(lesson.lessonDate, []);
    byDate.get(lesson.lessonDate)!.push(lesson);
  }

  const today = isoDate(new Date());
  const days = [...byDate.entries()].map(([date, items]) => {
    const weekday = WEEKDAY_NAMES[isoWeekday(parseIso(date)) - 1];
    const isToday = date === today;
    const rows = items.map(l => `
      <div class="assignment-row" style="border-left:3px solid ${esc(l.subjectColor)};margin-bottom:.375rem">
        <div style="display:flex;align-items:center;gap:.5rem;flex-wrap:wrap">
          <span style="font-weight:500;font-size:.9rem">${esc(l.subjectName)}</span>
          <span class="badge badge-blue">${esc(l.className)}</span>
          ${l.isPreliminary ? `<span class="badge">Vorläufig</span>` : ""}
        </div>
        <div style="font-size:.8rem;color:var(--gray-500);margin-top:.15rem">
          ${esc(l.startTime)} bis ${esc(l.endTime)}${l.room ? ` · Raum ${esc(l.room)}` : ""}
        </div>
      </div>`).join("");
    return `
      <div style="margin-bottom:1rem">
        <div style="font-size:.8125rem;font-weight:600;color:${isToday ? "var(--primary)" : "var(--gray-600)"};margin-bottom:.375rem">
          ${esc(weekday)}, ${esc(formatDate(date))}${isToday ? " · heute" : ""}
        </div>
        ${rows}
      </div>`;
  }).join("");

  host.innerHTML = `
    <p style="font-size:.8125rem;color:var(--gray-500);margin:0 0 1rem">
      Unterricht in den nächsten ${LESSON_WEEKS} Wochen. Geplant wird im Stundenplan der Klasse.
    </p>
    ${days}`;
}

export async function render(el: HTMLElement) {
  currentEl = el;
  searchQuery = "";
  el.innerHTML = `
    <div class="settings-page-header">
      <div>
        <h1>Dozenten</h1>
        <p>Dozenten dieser Schule mit ihren Anwesenheiten. Ist ein Benutzer verknüpft, kommen Name und E-Mail aus der Benutzerverwaltung.</p>
      </div>
      ${writeOnly(`<div class="settings-page-actions">
        <button class="btn btn-secondary" id="btn-csv">CSV-Import</button>
        <button class="btn btn-primary" id="btn-add">+ Neuer Dozent</button>
      </div>`)}
    </div>
    ${readOnlyNotice()}
    <div class="filter-bar"><input type="search" id="search-query" data-readable="true" placeholder="Suchen …" /></div>
    <div class="card table-wrap">
      <table>
        <thead><tr><th>Name</th><th>E-Mail</th><th style="width:110px">Art</th><th style="width:130px">Benutzer</th>${writeOnly(`<th style="width:120px">Aktionen</th>`)}</tr></thead>
        <tbody></tbody>
      </table>
    </div>`;

  try {
    await load();
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  renderTable();

  el.querySelector("#btn-add")?.addEventListener("click", () => openLecturerModal());
  el.querySelector("#btn-csv")?.addEventListener("click", () => openCsvImportModal({
    title: "Dozenten importieren",
    duplicateHint: "Dozenten mit gleichem Vor- und Nachnamen oder gleicher E-Mail werden übersprungen. Die Verknüpfung mit einem Benutzerkonto setzt du danach am Dozenten.",
    fieldDescriptions: [
      { field: "Vorname", hint: "Pflichtfeld" },
      { field: "Nachname", hint: "Pflichtfeld" },
      { field: "E-Mail", hint: "Optional" },
      { field: "Extern", hint: "Optional, „ja\" für Honorarkräfte und Gastdozenten" },
      { field: "Notiz", hint: "Optional" },
    ],
    onDownloadTemplate: () => api.stundenplan.lecturers.csvTemplate(),
    onImport: (file) => api.stundenplan.lecturers.csvImport(file),
    onComplete: async () => { await load(); renderTable(); },
  }));
  const searchInput = el.querySelector("#search-query") as HTMLInputElement;
  searchInput.addEventListener("input", () => { searchQuery = searchInput.value; renderTable(); });

  el.querySelector("tbody")!.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    const del = target.closest("[data-del]") as HTMLElement | null;
    if (del) {
      e.stopPropagation();
      const lecturer = lecturers.find(l => l.id === +del.dataset.del!);
      if (!lecturer) return;
      // Termine bleiben stehen und verlieren nur ihren Dozenten (ON DELETE SET NULL).
      // Das gehoert in den Dialog, sonst ist es eine stille Nebenwirkung.
      const affected = lecturer.lessonCount ?? 0;
      const ok = await confirmDialog({
        title: "Dozent löschen",
        message: `„${fullName(lecturer)}" wird mit allen Anwesenheiten und Ausnahmen gelöscht.`
          + (affected > 0
            ? ` ${affected} ${affected === 1 ? "Unterrichtstermin steht" : "Unterrichtstermine stehen"} danach ohne Dozent im Plan.`
            : ""),
        confirmLabel: "Löschen", danger: true,
      });
      if (!ok) return;
      try {
        await api.stundenplan.lecturers.delete(lecturer.id);
        await load();
        renderTable();
        showToast("Dozent gelöscht");
      } catch (err: any) { showToast("Fehler: " + err.message); }
      return;
    }

    const row = target.closest("[data-open]") as HTMLElement | null;
    if (row) {
      const lecturer = lecturers.find(l => l.id === +row.dataset.open!);
      if (lecturer) openLecturerModal(lecturer);
    }
  });
}

function showModalError(mb: HTMLElement, message: string) {
  const box = mb.querySelector("#f-modal-error") as HTMLElement | null;
  if (!box) { showToast(message); return; }
  box.textContent = message;
  box.classList.remove("hidden");
}
